import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { appRepo } from "../../repo/app-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { Pagination } from "../../lib/api.ts";
import PaginationView from "../../componrnts/pagination.tsx";
import { ASSETS_BASE_URL } from "../../utils/constants.ts";

function assetUrl(path: string) {
  if (!path) return "";
  return path.includes("http") ? path : `${ASSETS_BASE_URL}${path.replace(/^\//, "")}`;
}

export default function ImagesList() {
  const [images, setImages] = useState<string[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const { setMessage } = useContext(AppContext);

  const loadImages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await appRepo.getImages(page);
      if (!response.status || !response.data) {
        setMessage({ type: "error", message: response.message || "دریافت لیست تصاویر ناموفق بود." });
        setImages([]);
        setSelectedUrls([]);
        return;
      }
      setImages(response.data);
      setPagination(response.pagination ?? null);
      setSelectedUrls([]);
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      setImages([]);
      setSelectedUrls([]);
    } finally {
      setLoading(false);
    }
  }, [page, setMessage]);

  useEffect(() => {
    void loadImages();
  }, [loadImages]);

  const allSelected = useMemo(
    () => images.length > 0 && selectedUrls.length === images.length,
    [images.length, selectedUrls.length]
  );

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedUrls([]);
      return;
    }
    setSelectedUrls(images);
  };

  const toggleSelect = (url: string) => {
    setSelectedUrls((prev) => (prev.includes(url) ? prev.filter((item) => item !== url) : [...prev, url]));
  };

  const deleteUrls = async (urls: string[]) => {
    if (!urls.length) return;
    const count = urls.length;
    if (!window.confirm(`آیا مطمئن هستید که می‌خواهید ${count} تصویر را حذف کنید؟`)) {
      return;
    }

    setDeleting(true);
    try {
      const response = await appRepo.deleteImages(urls);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "حذف تصاویر ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: `${count} تصویر با موفقیت حذف شد.` });
      void loadImages();
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setDeleting(false);
    }
  };

  const onDeleteSelected = () => {
    void deleteUrls(selectedUrls);
  };

  const onDeleteOne = (url: string) => {
    void deleteUrls([url]);
  };

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setMessage({ type: "success", message: "آدرس تصویر در کلیپ‌بورد کپی شد." });
    } catch {
      setMessage({ type: "error", message: "کپی آدرس تصویر ناموفق بود." });
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <AdminPageTitle title="تصاویر" />
        {images.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={toggleSelectAll}
              className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
            >
              {allSelected ? "لغو انتخاب همه" : "انتخاب همه"}
            </button>
            <button
              type="button"
              onClick={onDeleteSelected}
              disabled={!selectedUrls.length || deleting}
              className="rounded-lg border border-red-700 px-4 py-2 text-sm text-red-200 transition hover:border-red-400 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {deleting ? "در حال حذف…" : `حذف انتخاب‌شده (${selectedUrls.length})`}
            </button>
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت لیست تصاویر...
        </div>
      ) : !images.length ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          تصویری یافت نشد.
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {images.map((image) => {
              const isSelected = selectedUrls.includes(image);
              return (
                <div
                  key={`${ASSETS_BASE_URL}${image}`}
                  className={`overflow-hidden rounded-2xl border bg-slate-900/60 transition ${
                    isSelected ? "border-indigo-500" : "border-slate-800"
                  }`}
                >
                  <label className="block cursor-pointer">
                    <div className="relative aspect-video bg-slate-950">
                      <img
                        src={assetUrl(image)}
                        alt={image}
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(image)}
                        className="absolute left-3 top-3 h-4 w-4 accent-indigo-500"
                      />
                    </div>
                  </label>
                  <div className="space-y-3 p-3">
                    <button
                      type="button"
                      onClick={() => copyUrl(image)}
                      className="w-full truncate text-start text-xs text-slate-400 transition hover:text-indigo-300"
                      title="برای کپی کلیک کنید"
                    >
                      {image}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteOne(image)}
                      disabled={deleting}
                      className="w-full rounded-lg border border-red-700 px-3 py-1.5 text-xs text-red-200 transition hover:border-red-400 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      حذف
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {pagination ? <PaginationView pagination={pagination} onPageChange={setPage} /> : null}
        </>
      )}
    </section>
  );
}
